import { useContext, useEffect, useState } from 'react';
import useFields from '../Hooks/useFields';
import {
    Card, CardContent, TextField, Button,
    Typography, Box, Container,
} from '@mui/material';
import API from '../api2';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useNotification from '../Hooks/useNotification';
import UserContext from './UserSettings/UserContext';
import checkPasswordStrength from './UserHelpers/passwordStrengthChecker'

function LoginCard() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [passwordReset, setPasswordReset] = useState(false);
    const { setUser: setContextUser } = useContext(UserContext) || { setUser: () => { } };
    const notify = useNotification();
    const [formData, setForm] = useFields({
        email: "",
        password: ""
    });

    useEffect(() => {
        if (token) {
            if (location.pathname.includes('/verify-email/')) {
                setPasswordReset(true);
                const verifyEmail = async () => {
                    const result = await API.verifyUser({ token });
                    if (!result.error) {
                        setForm('replaceKey', 'email', result.data.email);
                    }
                    notify(result.message, result.error ? "error" : "success");
                };
                verifyEmail();
            } else if (location.pathname.includes('/password-reset/')) {
                const resetPassword = async () => {
                    const result = await API.resetPasswordVerification({ token });
                    if (!result.error) {
                        setForm('replaceKey', 'email', result.data.email);
                    }
                    notify(result.message, result.error ? "error" : "success");
                }
                resetPassword();
                setPasswordReset(true);
            }
        }
    }, []);

    const handleSubmit = async function (e: any) {
        e.preventDefault();
        let result;
        if (passwordReset) {
            // Check password strength before submitting
            const strengthCheck = checkPasswordStrength(formData.password);
            if (!strengthCheck.passed) {
                return notify(strengthCheck.message, "error");
            }

            // Verify passwords match
            if (formData.password !== formData.verifyPassword) {
                return notify("Passwords do not match", "error");
            }

            result = await API.resetPassword({
                email: formData.email,
                password: formData.password,
                token,
                verifyPassword: formData.verifyPassword
            });
        } else {
            result = await API.loginUser(formData);
        }
        if (result.error) return notify(result.message, "error");

        // Handle password reset success differently from regular login
        if (passwordReset) {
            // Password reset successful - reset form and switch to login mode
            setForm('replaceAll', {
                email: formData.email,
                password: "",
                verifyPassword: ""
            });
            setPasswordReset(false);
            notify("Password has been reset successfully. Please log in with your new password.", "success");
            return;
        }

        // Regular login - set user state and navigate
        const theCurrentUser = { ...result.data, email: formData.email };
        API.token = result.data.token;

        if (window.innerWidth < 768) theCurrentUser.mobile = true;
        else theCurrentUser.mobile = false;

        const userAgent = navigator.userAgent.toLowerCase();
        if (userAgent.indexOf('android') > -1) {
            theCurrentUser.os = 'android';
        } else if (userAgent.indexOf('windows') > -1) {
            theCurrentUser.os = 'windows';
        } else if (userAgent.indexOf('iphone') > -1 || userAgent.indexOf('ipad') > -1) {
            theCurrentUser.os = 'ios';
        } else if (userAgent.indexOf('macintosh') > -1) {
            theCurrentUser.os = 'macintosh';
        } else {
            theCurrentUser.os = 'unknown';
        }
        setContextUser(theCurrentUser);

        navigate("/");
        return notify(result.message, result.error ? "error" : "success");

    };

    return (<>
        <form onSubmit={handleSubmit}>
            <Container maxWidth="sm">
                <Box sx={{ marginTop: 8 }}>
                    <Card elevation={3}>
                        <CardContent>
                            <Typography variant="h5" component="h1" gutterBottom textAlign="center">
                                {passwordReset ? "Reset Password" : "Login"}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    required
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    type="email"
                                    disabled={passwordReset}
                                    value={formData.email}
                                    onChange={setForm}
                                    autoComplete="email"
                                />
                                <TextField
                                    required
                                    fullWidth
                                    label={passwordReset ? "New Password" : "Password"}
                                    name="password"
                                    type="password"
                                    value={formData.password}
                                    onChange={setForm}
                                    autoComplete={passwordReset ? "new-password" : "current-password"}
                                />
                                {passwordReset && (
                                    <TextField
                                        required
                                        fullWidth
                                        label="Verify New Password"
                                        name="verifyPassword"
                                        type="password"
                                        value={formData.verifyPassword}
                                        onChange={setForm}
                                        autoComplete="new-password"
                                    />
                                )}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    sx={{ mt: 2 }}
                                >
                                    {passwordReset ? "Set New Password" : "LogIn"}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Container>
        </form>
    </>);
}

export default LoginCard;
